import dedent from 'ts-dedent'
import {
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { Workflow } from '../workflow'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class Call extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.call',
        group: 'Custom',
        title: 'Call Procedure',
        description: `
        This component executes an arbitrary CALL SQL statement.

        Use $a and $b as placeholders for the input table names
        and $output as placeholder for the output table name.

        `,
        starred: true
      },
      [
        {
          name: 'sourcea',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          optional: true
        },
        {
          name: 'sourceb',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          optional: true
        },
        {
          name: 'sql',
          type: WorkflowComponentParamType.StringSql,
          title: 'SQL CALL statement',
          mode: WorkflowComponentParameterMode.Multiline,
          placeholder: 'CALL MY_PROCEDURE($a, 10, $output);',
          allowExpressions: false
        }
      ]
    )
  }

  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Redshift, Provider.Postgres]
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (params.sql && !isExpressionValue(params.sql as WorkflowComponentValue)) {
      const missingTables: string[] = []
      const value = (params.sql as WorkflowComponentValue).value as string
      for (const source of ['sourcea', 'sourceb']) {
        if (value && value.includes(`$${source[source.length - 1]}`) && !resolvedParamValues[source]) {
          missingTables.push(source)
        }
      }
      if (missingTables.length > 0) {
        problems.push({
          parameter: 'sql',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: `Table${missingTables.length > 1 ? 's' : ''} ${missingTables
            .map((t) => `"${t[t.length - 1]}"`)
            .join(', ')} used in statement but not provided`
        })
      }
    }
    return problems
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const isStoredProcedure = codeType === WorkflowCodeType.StoredProcedure
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    // NOTE: avoid quote vars and leave quoting to the user doing
    // correct call
    let query = ((params.sql as string) ?? '')
      .replace(/\$a/g, params.sourcea as string)
      .replace(/\$b/g, params.sourceb as string)
      .replace(/\$output/g, tablename)
      .replace(/\$dryrun/g, dryRun.toString())
      .replace(/\$isStoredProcedure/g, isStoredProcedure.toString())
    query += query.endsWith(';') ? '' : ';'
    query = dedent`${connection.wrapCodeInIfTableExistsBlock(query, [tablename], codeType)}`
    return {
      query,
      outputs: { result: tablename }
    }
  }

  isOutputTableCreated(
    workflow: Workflow,
    componentName: string,
    outputName: string,
    params: Record<string, unknown>
  ): boolean {
    return /\$output/g.test(params.sql as string)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '3'
  }
}

export { Call }
